import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useMsal } from '@azure/msal-react';
import { apiRequest } from '../../auth/msalConfig';
import {
  MdClose,
  MdSave,
  MdRefresh,
  MdCheckCircle,
  MdWarningAmber,
  MdCode,
  MdInfoOutline,
  MdCheck,
} from '../../utils/icons';
import { unwrapEnvelope, StepApiError, fetchWithRetry, TRANSIENT_STATUS, TRANSIENT_MESSAGE } from './stepApi';
import './AttributeConfig.css';
import './PromptConfig.css';

// ── Spec ───────────────────────────────────────────────────────────────────
// "Prompt Configuration" screen (sibling of the Attributes panel). The extraction
// system prompt is now stored as TWO editable templates — one per channel (excel,
// pdf) — each a single markdown string that carries {{ATTRIBUTE_SCHEMA}} and
// {{TIER2_BLOCK}} placeholders resolved at run time.
// Backend: workflows/step_product_workflows/PROMPT_CONFIG_FE_INTEGRATION.md
//   GET {base} -> { ok:true, data:{ version, prompts:{ excel:{content,…}, pdf:{content,…} } } }
//   PUT {base} -> body { updated_by, base_version, excel:"…", pdf:"…" }  (whole-set replace)
//              -> { ok:true, data:{ saved, version, warnings } }
// Send base_version — a stale value is now rejected with 409 VERSION_CONFLICT.

// ── Types ────────────────────────────────────────────────────────────────────

type Channel = 'excel' | 'pdf';

interface ChannelMeta {
  version: number | null;
  updated_by: string | null;
  updated_at: string | null;
}

interface PromptConfigProps {
  open: boolean;
  onClose: () => void;
  updatedBy: string;
}

const CHANNELS: Channel[] = ['excel', 'pdf'];
const CHANNEL_LABEL: Record<Channel, string> = { excel: 'Excel', pdf: 'PDF' };
const CHANNEL_SUB: Record<Channel, string> = {
  excel: 'System prompt used when the source is an Excel / CSV document',
  pdf: 'System prompt used when the source is a PDF document',
};

// Placeholders resolved by the workflow at run time (kept as literal text here).
const TOKEN_SCHEMA = '{{ATTRIBUTE_SCHEMA}}';
const TOKEN_TIER2 = '{{TIER2_BLOCK}}';
const TOKENS: Array<{ token: string; hint: string; desc: string; required: boolean }> = [
  {
    token: TOKEN_SCHEMA,
    hint: 'the live STEP attribute list (from the Attributes panel)',
    desc: 'At run time this is replaced with the live STEP attribute list — the STEP IDs + guidance maintained in the ⚙ Attributes panel. Every prompt must contain it, or the model has no schema to map against.',
    required: true,
  },
  {
    token: TOKEN_TIER2,
    hint: 'OpCo-specific master data, injected per run',
    desc: 'At run time this is replaced with the OpCo-specific Tier-2 master data selected for that extraction run. Optional — omit it and the prompt still saves, but no per-OpCo master data is injected.',
    required: false,
  },
];

type Texts = Record<Channel, string>;
const EMPTY: Texts = { excel: '', pdf: '' };

// Ragged line widths for the load skeleton — reads as prose, not a progress bar.
const SKEL_WIDTHS = ['92%', '78%', '85%', '61%', '88%', '73%', '95%', '48%', '81%', '66%'];

// Any {{TOKEN}} placeholder — highlighted in the editor so they're easy to spot.
const TOKEN_RE = /\{\{[^{}]+\}\}/g;
const escapeHtml = (s: string): string =>
  s.replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;');
// Escape the text, then wrap placeholder tokens. Tokens contain no & < > so they
// survive escaping unchanged and still match. Trailing newline gets a guard char
// so the backdrop's last line height matches the textarea's.
const highlight = (s: string): string =>
  escapeHtml(s).replace(TOKEN_RE, (m) => `<mark class="pcfg-hl">${m}</mark>`) + '\n';

// ── Validation (mirror of server rules) ────────────────────────────────────────
// Hard rules (block Save, server 400): non-empty + must contain {{ATTRIBUTE_SCHEMA}}.
// Soft rule (saves, server returns it in warnings[]): missing {{TIER2_BLOCK}}.
interface Issue { channel: Channel; message: string; hard: boolean; }

// Count non-overlapping occurrences of a fixed token.
const countToken = (s: string, token: string): number => s.split(token).length - 1;

const validate = (text: Texts): Issue[] => {
  const issues: Issue[] = [];
  for (const ch of CHANNELS) {
    const t = text[ch];
    if (t.trim() === '') {
      issues.push({ channel: ch, message: `${CHANNEL_LABEL[ch]} prompt is empty.`, hard: true });
      continue;
    }
    const nSchema = countToken(t, TOKEN_SCHEMA);
    const nTier2 = countToken(t, TOKEN_TIER2);
    if (nSchema === 0) {
      issues.push({ channel: ch, message: `${CHANNEL_LABEL[ch]} prompt must contain the ${TOKEN_SCHEMA} placeholder.`, hard: true });
    } else if (nSchema > 1) {
      issues.push({ channel: ch, message: `${CHANNEL_LABEL[ch]} prompt uses ${TOKEN_SCHEMA} ${nSchema} times — only one is allowed.`, hard: true });
    }
    if (nTier2 === 0) {
      issues.push({ channel: ch, message: `${CHANNEL_LABEL[ch]} prompt has no ${TOKEN_TIER2} placeholder — it will save, but there is no OpCo master-data injection point.`, hard: false });
    } else if (nTier2 > 1) {
      issues.push({ channel: ch, message: `${CHANNEL_LABEL[ch]} prompt uses ${TOKEN_TIER2} ${nTier2} times — only one is allowed.`, hard: true });
    }
  }
  return issues;
};

// ── Helpers ────────────────────────────────────────────────────────────────────

const toMeta = (o: any): ChannelMeta => ({
  version: typeof o?.version === 'number' ? o.version : null,
  updated_by: typeof o?.updated_by === 'string' ? o.updated_by : null,
  updated_at: typeof o?.updated_at === 'string' ? o.updated_at : null,
});

const fmtWhen = (iso: string | null): string => {
  if (!iso) return '';
  // ISO → "YYYY-MM-DD HH:MM" (display only; avoids locale/timezone surprises).
  const m = /^(\d{4}-\d{2}-\d{2})T(\d{2}:\d{2})/.exec(iso);
  return m ? `${m[1]} ${m[2]}` : iso;
};

// ── Component ────────────────────────────────────────────────────────────────

const PromptConfig: React.FC<PromptConfigProps> = ({ open, onClose, updatedBy }) => {
  const configUrl = process.env.REACT_APP_PRODUCT_AGENT_WEBHOOK_URL + '/webhook/step-prompt-config';

  const { instance, accounts } = useMsal();

  // Acquire an AAD access token for the API scope. APIM rejects requests without
  // it (401 Unauthorized). Returns null when a redirect was triggered (silent
  // acquisition failed) — caller must bail out.
  const acquireApiToken = useCallback(async (): Promise<string | null> => {
    try {
      const tokenResponse = await instance.acquireTokenSilent({
        ...apiRequest,
        account: accounts[0],
      });
      return tokenResponse.accessToken;
    } catch {
      await instance.acquireTokenRedirect(apiRequest);
      return null;
    }
  }, [instance, accounts]);

  const [text, setText] = useState<Texts>(EMPTY);
  const [meta, setMeta] = useState<Record<Channel, ChannelMeta>>({
    excel: toMeta(null), pdf: toMeta(null),
  });
  const [baseVersion, setBaseVersion] = useState<number | null>(null);
  const [active, setActive] = useState<Channel>('excel');
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [loadError, setLoadError] = useState<string | null>(null);
  const [saveWarnings, setSaveWarnings] = useState<string[]>([]);
  const [savedFlash, setSavedFlash] = useState(false);
  const [pristine, setPristine] = useState<string>(JSON.stringify(EMPTY));
  const taRefs = useRef<Record<Channel, HTMLTextAreaElement | null>>({ excel: null, pdf: null });
  const backdropRef = useRef<HTMLDivElement | null>(null);
  // Last known caret/selection in the active editor — survives the button click
  // that would otherwise blur the textarea and collapse the caret to 0.
  const selRef = useRef<{ start: number; end: number }>({ start: 0, end: 0 });

  // Keep the highlight backdrop scrolled in lock-step with the textarea.
  const syncScroll = () => {
    const ta = taRefs.current[active];
    const bd = backdropRef.current;
    if (ta && bd) { bd.scrollTop = ta.scrollTop; bd.scrollLeft = ta.scrollLeft; }
  };

  const dirty = useMemo(() => JSON.stringify(text) !== pristine, [text, pristine]);
  const base = useMemo<Texts>(() => {
    try { return JSON.parse(pristine) as Texts; } catch { return EMPTY; }
  }, [pristine]);
  const chDirty = (ch: Channel) => text[ch] !== base[ch];

  // While a load is in flight (or after one failed) the editors hold EMPTY,
  // which would trip the "prompt is empty" hard rule and stack a bogus banner on
  // top of the real status. Validation describes what the user typed, so it stays
  // silent until there is loaded content or an edit to judge.
  const issues = useMemo(
    () => (loading || (loadError !== null && !dirty) ? [] : validate(text)),
    [text, loading, loadError, dirty],
  );
  const hardIssues = useMemo(() => issues.filter((i) => i.hard), [issues]);
  const warnIssues = useMemo(() => issues.filter((i) => !i.hard), [issues]);
  const canSave = dirty && !saving && !loading && hardIssues.length === 0;

  // ── Load ──
  const load = useCallback(async () => {
    if (!configUrl) { setLoadError('REACT_APP_PRODUCT_AGENT_WEBHOOK_URL is not set.'); return; }
    setLoading(true);
    setLoadError(null);
    try {
      const token = await acquireApiToken();
      if (!token) return; // redirect triggered
      const res = await fetchWithRetry(() => fetch(configUrl, {
        method: 'GET',
        headers: { 'Authorization': `Bearer ${token}` },
      }));
      const data = await unwrapEnvelope(res); // { version, prompts }
      const p = data?.prompts ?? {};
      const next: Texts = {
        excel: typeof p.excel?.content === 'string' ? p.excel.content : '',
        pdf: typeof p.pdf?.content === 'string' ? p.pdf.content : '',
      };
      setText(next);
      setPristine(JSON.stringify(next));
      setBaseVersion(typeof data?.version === 'number' ? data.version : null);
      setMeta({ excel: toMeta(p.excel), pdf: toMeta(p.pdf) });
    } catch (err: any) {
      setLoadError(err instanceof StepApiError && TRANSIENT_STATUS.has(err.status)
        ? TRANSIENT_MESSAGE
        : `Failed to load prompts. (${err?.message ?? 'unknown error'})`);
      setText(EMPTY);
      setPristine(JSON.stringify(EMPTY));
      setBaseVersion(null);
      setMeta({ excel: toMeta(null), pdf: toMeta(null) });
    } finally {
      setLoading(false);
    }
  }, [configUrl, acquireApiToken]);

  useEffect(() => { if (open) load(); }, [open, load]);

  // Esc closes (only when clean).
  useEffect(() => {
    if (!open) return;
    const onKey = (e: KeyboardEvent) => { if (e.key === 'Escape' && !dirty) onClose(); };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [open, dirty, onClose]);

  const setContent = (ch: Channel, v: string) =>
    setText((prev) => ({ ...prev, [ch]: v }));

  // On channel switch, park the caret at the end of the newly-shown template so
  // an insert without an explicit click appends rather than lands at a stale offset.
  useEffect(() => {
    const len = text[active].length;
    selRef.current = { start: len, end: len };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [active]);

  // ── Save (PUT — whole set, both channels, replace-semantics) ──
  const save = async () => {
    if (hardIssues.length > 0) { setActive(hardIssues[0].channel); return; }
    if (!configUrl) { setLoadError('REACT_APP_PRODUCT_AGENT_WEBHOOK_URL is not set.'); return; }

    setSaving(true);
    setLoadError(null);
    setSaveWarnings([]);
    try {
      const token = await acquireApiToken();
      if (!token) { setSaving(false); return; } // redirect triggered

      // base_version is sent below; the server now rejects a stale write with
      // 409 VERSION_CONFLICT, so the old best-effort pre-check GET is gone.
      const res = await fetchWithRetry(() => fetch(configUrl, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${token}` },
        body: JSON.stringify({
          updated_by: updatedBy || 'unknown',
          base_version: baseVersion,
          excel: text.excel,
          pdf: text.pdf,
        }),
      }));
      const data = await unwrapEnvelope(res); // { saved, version, warnings }
      const warnings = Array.isArray(data?.warnings) ? data.warnings : [];
      await load();
      setSaveWarnings(warnings);
      setSavedFlash(true);
      setTimeout(() => setSavedFlash(false), 2500);
    } catch (err: any) {
      if (err instanceof StepApiError && err.code === 'VERSION_CONFLICT') {
        const cur = err.details?.current_version;
        setLoadError(
          `Prompts changed on the server${cur != null ? ` (now version ${cur}, you loaded ${baseVersion})` : ''}. Reload before saving so you don't overwrite their changes.`,
        );
      } else if (err instanceof StepApiError && TRANSIENT_STATUS.has(err.status)) {
        setLoadError(`${TRANSIENT_MESSAGE} Nothing was written — your changes are still here.`);
      } else {
        setLoadError(`Save failed — nothing was written. (${err?.message ?? 'unknown error'})`);
      }
    } finally {
      setSaving(false);
    }
  };

  const discard = () => { setText(base); setSaveWarnings([]); };
  const reload = () => { setSaveWarnings([]); load(); };

  // Insert a placeholder token at the caret in the active editor, preserving the
  // scroll position (button mousedown is prevented so focus stays put; the caret
  // comes from selRef, not from the blurred textarea).
  const insertToken = (token: string) => {
    const ta = taRefs.current[active];
    const cur = text[active];
    const s = Math.min(selRef.current.start, cur.length);
    const e = Math.min(selRef.current.end, cur.length);
    setContent(active, cur.slice(0, s) + token + cur.slice(e));
    const pos = s + token.length;
    selRef.current = { start: pos, end: pos };
    requestAnimationFrame(() => {
      if (!ta) return;
      ta.focus();
      ta.setSelectionRange(pos, pos);
      syncScroll();
    });
  };

  if (!open) return null;

  const activeText = text[active];
  const activeMeta = meta[active];

  return (
    <div className="acfg-overlay" role="dialog" aria-modal="true" aria-label="Prompt Configuration">
      <div className="acfg-backdrop" onClick={() => { if (!dirty) onClose(); }} />
      <div className="acfg-panel">

        {/* ── Header ── */}
        <div className="acfg-head">
          <div className="acfg-head-left">
            <h2 className="acfg-title">Prompt Configuration</h2>
            <p className="acfg-subtitle">The full extraction system prompt — one editable template per channel</p>
          </div>
          <button className="acfg-close" onClick={() => { if (!dirty) onClose(); }} aria-label="Close"
            title={dirty ? 'Save or discard changes first' : 'Close'} disabled={dirty}>
            <MdClose aria-hidden="true" />
          </button>
        </div>

        {/* ── Toolbar: channel tabs + token helpers ── */}
        <div className="acfg-toolbar">
          <div className="pcfg-chanfilter" role="tablist" aria-label="Prompt channel">
            {CHANNELS.map((ch) => (
              <button
                key={ch}
                role="tab"
                aria-selected={active === ch}
                className={`pcfg-chanfilter-btn${active === ch ? ' pcfg-chanfilter-btn--on' : ''}`}
                onClick={() => setActive(ch)}
              >
                {CHANNEL_LABEL[ch]}
                {chDirty(ch) && <span className="pcfg-tab-dot" title="Unsaved changes" />}
              </button>
            ))}
          </div>

          <span className="pcfg-tokenbar-label">
            <MdCode aria-hidden="true" />Insert placeholder:
            <span className="pcfg-info" tabIndex={0} role="button" aria-label="What are placeholders?">
              <MdInfoOutline aria-hidden="true" />
              <span className="pcfg-info-pop" role="tooltip">
                <strong>Placeholders</strong> are tokens the extraction workflow swaps for live data
                at run time. Leave them as-is in the prompt — the model receives the resolved data,
                not the token text. Each placeholder may be used <strong>at most once</strong> per prompt.
                <ul>
                  {TOKENS.map((t) => (
                    <li key={t.token}>
                      <code>{t.token}</code>
                      <span className={`pcfg-info-tag${t.required ? ' pcfg-info-tag--req' : ''}`}>
                        {t.required ? 'required' : 'optional'}
                      </span>
                      <br />{t.desc}
                    </li>
                  ))}
                </ul>
              </span>
            </span>
          </span>
          {TOKENS.map((t) => {
            const inUse = activeText.includes(t.token);
            return (
              <button
                key={t.token}
                className={`pcfg-token-btn${inUse ? ' pcfg-token-btn--used' : ''}`}
                onMouseDown={(e) => e.preventDefault()}
                onClick={() => insertToken(t.token)}
                disabled={loading || saving || inUse}
                title={inUse
                  ? `${t.token} is already in use — only one is allowed per prompt`
                  : `Insert ${t.token} — ${t.hint}`}
              >
                {t.token}
                {inUse && <span className="pcfg-token-used-tag"><MdCheck aria-hidden="true" />in use</span>}
              </button>
            );
          })}

          <button className="acfg-btn acfg-btn--ghost pcfg-toolbar-right" onClick={reload} disabled={loading || saving}
            title="Reload from server (discards edits)">
            <MdRefresh aria-hidden="true" />Reload
          </button>
        </div>

        {/* ── Status line ── */}
        <div className="acfg-status">
          <span className="acfg-status-item">
            Version <strong>{baseVersion ?? '—'}</strong>
          </span>
          <span className="acfg-status-sep">·</span>
          <span className="acfg-status-item">
            <strong>{CHANNEL_LABEL[active]}</strong>: {activeText.length.toLocaleString()} chars
          </span>
          {activeMeta.updated_by && (
            <>
              <span className="acfg-status-sep">·</span>
              <span className="acfg-status-item">
                last edited by <strong>{activeMeta.updated_by}</strong>
                {activeMeta.updated_at && ` · ${fmtWhen(activeMeta.updated_at)}`}
              </span>
            </>
          )}
          {dirty && <span className="acfg-dirty">● Unsaved changes</span>}
          {savedFlash && <span className="acfg-saved"><MdCheckCircle aria-hidden="true" />Saved</span>}
        </div>

        {/* ── Errors / warnings ── */}
        {loadError && (
          <div className="acfg-banner acfg-banner--error">
            <MdWarningAmber aria-hidden="true" />{loadError}
          </div>
        )}
        {hardIssues.map((i) => (
          <div key={`h-${i.channel}-${i.message}`} className="acfg-banner acfg-banner--error">
            <MdWarningAmber aria-hidden="true" />{i.message}
          </div>
        ))}
        {warnIssues.map((i) => (
          <div key={`w-${i.channel}-${i.message}`} className="acfg-banner acfg-banner--warn">
            <MdWarningAmber aria-hidden="true" />{i.message}
          </div>
        ))}
        {saveWarnings.map((w, idx) => (
          <div key={`sw-${idx}`} className="acfg-banner acfg-banner--warn">
            <MdWarningAmber aria-hidden="true" />Saved with warning: {w}
          </div>
        ))}

        {/* ── Body: one large template editor for the active channel ── */}
        <div className="pcfg-editor-wrap">
          <p className="pcfg-editor-sub">{CHANNEL_SUB[active]}</p>
          {loading ? (
            /* Skeleton stands in for the editor while the prompts are fetched —
               same box as .pcfg-editor-shell so nothing jumps on swap. */
            <div className="pcfg-skel" role="status" aria-live="polite" aria-busy="true">
              <div className="pcfg-skel-lines" aria-hidden="true">
                {SKEL_WIDTHS.map((w, i) => (
                  <span key={i} className="pcfg-skel-line" style={{ width: w }} />
                ))}
              </div>
              <div className="pcfg-skel-note">
                <span className="pcfg-spinner" aria-hidden="true" />
                Loading {CHANNEL_LABEL[active]} prompt…
              </div>
            </div>
          ) : (
            <div className="pcfg-editor-shell">
              <div
                className="pcfg-editor-backdrop"
                ref={backdropRef}
                aria-hidden="true"
                dangerouslySetInnerHTML={{ __html: highlight(activeText) }}
              />
              <textarea
                className="pcfg-editor"
                ref={(el) => { taRefs.current[active] = el; }}
                value={activeText}
                onChange={(e) => {
                  setContent(active, e.target.value);
                  selRef.current = { start: e.target.selectionStart, end: e.target.selectionEnd };
                }}
                onSelect={(e) => {
                  const t = e.currentTarget;
                  selRef.current = { start: t.selectionStart, end: t.selectionEnd };
                }}
                onScroll={syncScroll}
                placeholder={`Full ${CHANNEL_LABEL[active]} system prompt (markdown, injected verbatim). Keep the ${TOKEN_SCHEMA} placeholder.`}
                spellCheck={false}
              />
            </div>
          )}
        </div>

        {/* ── Footer ── */}
        <div className="acfg-footer">
          <p className="acfg-footer-hint">
            Save replaces both prompt templates. Each must keep the <code className="pcfg-code">{TOKEN_SCHEMA}</code> placeholder.
          </p>
          <div className="acfg-footer-actions">
            <button className="acfg-btn acfg-btn--ghost" onClick={discard} disabled={!dirty || saving || loading}>
              Discard
            </button>
            <button className="acfg-btn acfg-btn--save" onClick={save} disabled={!canSave}>
              <MdSave aria-hidden="true" />{saving ? 'Saving…' : 'Save'}
            </button>
          </div>
        </div>

      </div>
    </div>
  );
};

export default PromptConfig;
